import * as stylex from '@stylexjs/stylex';
import { ReactNode, useRef, useEffect } from 'react';
import { PlusIcon } from '@shared/assets/icons';
import { styles } from '../../styles';
import { ElementTypeEnum } from '@entities/leadCollector/model/consts';
import { ElementsTemplateGroupedByType } from '@entities/leadCollector/lib/hooks/useFetchElementsTemplates';

const ELEMENT_TYPE_TRANSLATIONS: Record<ElementTypeEnum, string> = {
  [ElementTypeEnum.LOGO]: 'Логотип',
  [ElementTypeEnum.IMAGE]: 'Изображение',
  [ElementTypeEnum.TITLE]: 'Заголовок',
  [ElementTypeEnum.TEXT]: 'Текст',
  [ElementTypeEnum.FORMS]: 'Карточка гостя',
  [ElementTypeEnum.CHECKBOX]: 'Множественный выбор',
  [ElementTypeEnum.RADIOBUTTON]: 'Одиночный выбор',
  [ElementTypeEnum.BUTTON]: 'Кнопка',
};

const getElementTypeLabel = (type: ElementTypeEnum): string => {
  return ELEMENT_TYPE_TRANSLATIONS[type] || type;
};

interface FormElementItemProps {
  group: ElementsTemplateGroupedByType;
  isHovered: boolean;
  isSelected: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  onClose: () => void;
  renderContent?: (
    elementType: ElementTypeEnum,
    elements: ElementsTemplateGroupedByType['elements'],
    index: number
  ) => ReactNode;
  index: number;
}

export const FormElementItem = ({
  group,
  isHovered,
  isSelected,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onClose,
  renderContent,
  index,
}: FormElementItemProps) => {
  const itemRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isSelected) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (itemRef.current && !itemRef.current.contains(target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSelected, onClose]);

  const content = renderContent?.(group.type, group.elements, index);
  const hasContent = content != null;

  return (
    <div
      ref={itemRef}
      {...stylex.props(styles.elementItem)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <span>{getElementTypeLabel(group.type)}</span>
      <span {...stylex.props(styles.plusIcon, isHovered && styles.plusIconVisible)}>
        <PlusIcon width="18" height="18" />
      </span>
      {isSelected && hasContent && renderContent?.(group.type, group.elements, index) && (
        <div {...stylex.props(styles.popup)}>
          <div {...stylex.props(styles.popupContent)}>{content}</div>
        </div>
      )}
    </div>
  );
};
